/**
 * PlanDefaultsTab — V14.78 (Admin).
 *
 * Paramétrage des dimensions PAR DÉFAUT utilisées par l'Édition Plan :
 * épaisseurs de murs (cm), largeurs de menuiseries (cm), HSP standard (m).
 * Appliquées à la pose sur les plans calibrés.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ruler, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WALL_LABELS = {
  porteur: 'Mur porteur',
  cloison_fine: 'Cloison fine',
  cloison_basse: 'Cloison basse',
};
const OPENING_LABELS = {
  front_door: "Porte d'entrée",
  interior_door: 'Porte intérieure',
  closet_door: 'Porte de placard',
  french_door: 'Porte-fenêtre',
  window: 'Fenêtre classique',
  bay_window: 'Baie vitrée',
};

export default function PlanDefaultsTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/plan-defaults`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Impossible de charger les défauts plan.'));
  }, []);

  const setWall = (k, v) => setData((d) => ({ ...d, walls: { ...d.walls, [k]: v } }));
  const setOpening = (k, v) => setData((d) => ({ ...d, openings: { ...d.openings, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        walls: Object.fromEntries(Object.entries(data.walls).map(([k, v]) => [k, parseFloat(v)])),
        openings: Object.fromEntries(Object.entries(data.openings).map(([k, v]) => [k, parseFloat(v)])),
        default_hsp_m: parseFloat(data.default_hsp_m),
      };
      const { data: fresh } = await axios.put(`${API}/admin/plan-defaults`, payload, { withCredentials: true });
      setData(fresh);
      toast.success('Défauts plan enregistrés.');
    } catch (e) {
      toast.error('Erreur', { description: e?.response?.data?.detail || 'Réessayez.' });
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="plan-defaults-tab">
      <div className="flex items-start gap-3">
        <Ruler className="w-5 h-5 text-amber-600 mt-0.5" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Dimensions par défaut — Édition Plan</h2>
          <p className="text-sm text-gray-500">
            Appliquées automatiquement à la pose de murs et menuiseries sur les plans <strong>calibrés</strong>.
            Chaque élément reste modifiable individuellement dans l'inspecteur de l'éditeur.
          </p>
        </div>
      </div>

      {/* Murs */}
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Épaisseur des murs (cm)</h3>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(WALL_LABELS).map(([k, label]) => (
            <label key={k} className="text-xs text-gray-500 flex flex-col gap-1">
              {label}
              <div className="flex items-center gap-1">
                <input
                  type="number" min={1} max={100} step={1}
                  value={data.walls[k] ?? ''}
                  onChange={(e) => setWall(k, e.target.value)}
                  className="w-full h-9 px-2 text-sm rounded border border-gray-300 text-gray-900 focus:outline-none focus:border-amber-500"
                  data-testid={`plan-default-wall-${k}`}
                />
                <span className="text-gray-400">cm</span>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* Menuiseries */}
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Largeur des menuiseries (cm)</h3>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(OPENING_LABELS).map(([k, label]) => (
            <label key={k} className="text-xs text-gray-500 flex flex-col gap-1">
              {label}
              <div className="flex items-center gap-1">
                <input
                  type="number" min={20} max={600} step={5}
                  value={data.openings[k] ?? ''}
                  onChange={(e) => setOpening(k, e.target.value)}
                  className="w-full h-9 px-2 text-sm rounded border border-gray-300 text-gray-900 focus:outline-none focus:border-amber-500"
                  data-testid={`plan-default-opening-${k}`}
                />
                <span className="text-gray-400">cm</span>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* HSP */}
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Hauteur Sous Plafond standard (m)</h3>
        <p className="text-xs text-gray-500 mb-2">
          Appliquée aux nouveaux étages RDC / R+1 (sous-sol 2.30 m et combles 2.20 m restent typologiques).
        </p>
        <div className="flex items-center gap-1 w-40">
          <input
            type="number" min={1.8} max={4} step={0.05}
            value={data.default_hsp_m ?? ''}
            onChange={(e) => setData((d) => ({ ...d, default_hsp_m: e.target.value }))}
            className="w-full h-9 px-2 text-sm rounded border border-gray-300 text-gray-900 focus:outline-none focus:border-amber-500"
            data-testid="plan-default-hsp"
          />
          <span className="text-gray-400">m</span>
        </div>
      </section>

      <Button onClick={save} disabled={saving} data-testid="plan-defaults-save"
              className="bg-amber-600 hover:bg-amber-700 text-white">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Enregistrer
      </Button>
    </div>
  );
}
